import React from 'react';
import { ExternalLink, Shield, Phone, Globe, BookOpen, AlertTriangle } from 'lucide-react';
import { australianResources, glossary } from '../data/courseData';

export default function ResourcesPage() {
  return (
    <div className="max-w-4xl mx-auto fade-in">
      {/* Header */}
      <header className="mb-12">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Globe className="w-4 h-4" />
          <span>Trusted Australian Resources</span>
        </div>
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-text mb-4">
          Resources & Help
        </h1>
        <p className="text-text-muted text-lg">
          Trusted Australian resources to help you stay safe online and get help when you need it.
        </p>
      </header>

      {/* Emergency Help */}
      <section className="mb-12">
        <div className="bg-error/5 border-2 border-error/20 rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-error/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-error" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-text mb-1">
                Need Urgent Help?
              </h2>
              <p className="text-text-muted">
                If you've been scammed or need immediate assistance
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 border border-error/10">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-5 h-5 text-error" />
                <span className="font-bold text-text">Contact Your Bank Immediately</span>
              </div>
              <p className="text-text-muted text-sm">
                If you've shared financial details or made a payment to a scammer
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-error/10">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-error" />
                <span className="font-bold text-text">IDCARE: 1800 595 160</span>
              </div>
              <p className="text-text-muted text-sm">
                Free national identity & cyber support service
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Australian Resources */}
      <section className="mb-12">
        <h2 className="font-heading font-bold text-2xl text-text mb-6">
          Trusted Australian Websites
        </h2>
        <div className="space-y-4">
          {australianResources.map((resource, index) => (
            <a
              key={index}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`resource-${index}`}
              className="block bg-white rounded-2xl border-2 border-stone-200 p-6 hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-heading font-bold text-lg text-text mb-2 group-hover:text-primary transition-colors">
                    {resource.name}
                  </h3>
                  <p className="text-text-muted mb-2">{resource.description}</p>
                  <span className="text-primary text-sm font-medium">{resource.url}</span>
                </div>
                <ExternalLink className="w-5 h-5 text-stone-400 group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Glossary */}
      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-info" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-text">Glossary</h2>
        </div>
        
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {glossary.map((item, index) => (
            <div 
              key={index}
              className={`p-6 ${index !== glossary.length - 1 ? 'border-b border-stone-100' : ''}`}
            >
              <h3 className="font-heading font-bold text-lg text-primary mb-2">
                {item.term}
              </h3>
              <p className="text-text-muted">{item.definition}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workbook prompts */}
      <section className="mb-12">
        <div className="bg-primary/5 rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-4 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_aussiesafety/artifacts/17uanq9m_BTA_Logo_NoBgnd.png"
              alt="Bellarine Tech Assist"
              className="w-12 h-12 object-contain"
            />
            <div>
              <h2 className="font-heading font-bold text-xl text-text mb-1">
                Want More Resources?
              </h2>
              <p className="text-text-muted">
                The complete Digital Safety Workbook includes additional exercises, checklists, and space for your notes.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-digital-edition?variant=56047764865190"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 bg-white rounded-xl p-4 border border-primary/20 hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-bold text-text block group-hover:text-primary transition-colors">Digital Workbook</span>
                  <span className="text-text-muted text-sm">Download the eBook</span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-stone-400 group-hover:text-primary transition-colors" />
            </a>
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-printed-edition?variant=56047888105638"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 bg-white rounded-xl p-4 border border-primary/20 hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-bold text-text block group-hover:text-primary transition-colors">Printed Workbook</span>
                  <span className="text-text-muted text-sm">Order a physical copy</span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-stone-400 group-hover:text-primary transition-colors" />
            </a>
          </div>
        </div>
      </section>

      {/* Credits */}
      <section className="text-center bg-stone-50 rounded-2xl p-8">
        <p className="text-text-muted text-sm mb-2">
          Course content based on the "Digital Safety Workbook"
        </p>
        <p className="font-heading font-bold text-text">
          by Bellarine Tech Assist
        </p>
        <p className="text-text-muted text-sm mt-4">
          This information is for general educational purposes only. 
          For professional advice, consult with qualified experts.
        </p>
      </section>
    </div>
  );
}
